/**
 * Kabinetga Smart Forestry USB tokeni bilan kirish (WebAuthn / FIDO2).
 *
 * Token USB portga ulanadi, foydalanuvchi PIN kiritadi — parol va qo'shimcha kod kerak emas.
 * Token egasi hisobining barcha huquqlari bilan, to'liq tizimga kiradi. Token oldindan
 * ro'yxatdan o'tgan bo'lishi shart (Profil → USB token): tizimga ro'yxatdan o'tmagan kalit kirmaydi.
 */
import { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { homeRoute, useAuth } from "@/app/AuthContext";
import { ApiError } from "@/shared/api/client";
import { authApi } from "@/shared/api/endpoints";
import { useI18n } from "@/shared/i18n";
import { getAssertion, tokenErrorKey, usbTokenSupported } from "@/shared/lib/webauthn";
import { Icon } from "@/shared/ui/Icon";

export function UsbTokenLogin({ next }: { next: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [busy, setBusy] = useState<"" | "token" | "login">("");
  const [error, setError] = useState("");
  const supported = usbTokenSupported();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy("token");
    try {
      const options = await authApi.tokenLoginOptions();
      const credential = await getAssertion(options);
      setBusy("login");
      const result = await authApi.tokenLogin({ state: String(options.state), credential });
      setSession(result.user);
      navigate(next || homeRoute(result.user), { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.isOffline ? t("auth.err.network") : caught.message);
      } else {
        setError(t(tokenErrorKey(caught)));
      }
      setBusy("");
    }
  }

  const working = busy !== "";

  return (
    <form className="oneid-card eri-card" onSubmit={submit} noValidate>
      <div className="oneid-head">
        <span className="oneid-logo eri-logo">
          <Icon name="usb" size={22} />
        </span>
        <div>
          <b>{t("token.title")}</b>
          <span>{t("token.sub")}</span>
        </div>
      </div>

      {error ? (
        <div className="alert alert-error" role="alert">
          <Icon name="alert" size={18} />
          <span>{error}</span>
        </div>
      ) : null}

      {!supported ? (
        <div className="alert alert-info">
          <Icon name="info" size={18} />
          <span>{t("token.unsupported")}</span>
        </div>
      ) : (
        <>
          <ol className="token-steps">
            <li>{t("token.step1")}</li>
            <li>{t("token.step2")}</li>
          </ol>

          <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={working}>
            {busy === "token" ? t("token.working") : busy === "login" ? t("auth.submitting") : t("token.submit")}
            {working ? null : <Icon name="usb" size={18} />}
          </button>

          <p className="hint">{t("token.notRegistered")}</p>
        </>
      )}
    </form>
  );
}
